import React from "react";
import { of, Subject } from "rxjs";
import noop from "lodash/noop";
import {
  catchError,
  debounceTime,
  filter,
  map,
  switchMap,
} from "rxjs/operators";
import PropTypes from "prop-types";
import Autocomplete from "../../../../components/atoms/Autocomplete";
import apiClient from "../../../../services/apiClient";

function addKey(arr) {
  return arr.map((x, i) => ({ ...x, key: `${i}-${new Date().getTime()}` }));
}

function handleQueryError() {
  return of([]);
}

function search(searchQuery) {
  return apiClient.getPhenSuggestions(searchQuery).pipe(
    map((items) => addKey(items)),
    map((p) => p.map((x) => ({ ...x, phenotype_name: x.name }))),
    map((x) => ({ phenotypes: x }))
  );
}

class HPOSelector extends React.Component {
  constructor() {
    super();
    this.search$ = new Subject();
    this.inputTxtRef = React.createRef();
    this.state = { phenotypes: [] };
    this.register();
  }

  componentDidUpdate(prevProps) {
    const { selectedSource } = this.props;
    if (
      prevProps.selectedSource &&
      prevProps.selectedSource.length !== 0 &&
      JSON.stringify(prevProps.selectedSource) !==
        JSON.stringify(selectedSource)
    ) {
      this.setState({ phenotypes: [] });
      if (this.inputTxtRef?.current) {
        this.inputTxtRef.current.value = "";
      }
    }
  }

  getSearch = (query) => {
    const { selectedSource } = this.props;
    const phens = selectedSource.filter((phen) => phen !== "all");
    const sources = phens.length ? phens.join(",") : undefined;
    this.search$.next({ query, phenotypes_sources: sources });
  };

  filterItems = (items) => {
    const { hpo } = this.props;

    const { diseases: dis, phenotypes: phen } = hpo || {};
    // After mergin diseases with phenotypes all entries will be returned below phenotypes key
    if (items.phenotypes) {
      const result = items.phenotypes;
      const pSet = phen.reduce((a, i) => a.add(i.id), new Set());
      const dSet = dis.reduce((a, i) => a.add(i.disease_id), new Set());
      const phenotypes = result.filter((d) => !pSet.has(d.id));
      const res = phenotypes.filter((d) => !dSet.has(d.id));
      this.setState({ phenotypes: res });
    }
  };

  register = () => {
    this.search$
      .pipe(
        debounceTime(200), // wait for user to stop typing for 200 ms
        filter(({ query }) => query.length > 1), // check if input is more than two chars
        switchMap((x) => search(x)), // perform the search and wait to resolve
        catchError((e) => handleQueryError(e)), // handle error
        map((x) => this.filterItems(x)) // remove items that have been already added
      )
      .subscribe(noop, () => this.register());
  };

  addPhenotype = (p) => {
    const { addPhenotype } = this.props;
    const { phenotypes: current } = this.state;
    const inputId = p.disease_id || p.id;
    const phenotypes = current.filter((x) => x.id !== inputId);
    addPhenotype(p);
    this.setState({ phenotypes });
  };

  addDisease = (d) => {
    const { addDisease } = this.props;
    const { phenotypes } = this.state;
    const inputId = d.disease_id || d.id;
    const diseases = phenotypes.filter((x) => x.id !== inputId);
    addDisease(d);
    this.setState({ phenotypes: diseases });
  };

  handleClick = (item) => {
    if (item && item.mondo_id) {
      const newItem = { ...item, disease_id: item.id };
      this.addDisease(newItem);
    } else {
      this.addPhenotype(item);
    }
  };

  render() {
    const { phenotypes } = this.state;
    return (
      <div className="tw-flex tw-mb-4">
        <div className="tw-flex-grow tw-min-w-52">
          <Autocomplete
            optionKey="key"
            options={phenotypes}
            onSelect={(x) => {
              this.handleClick(x);
              if (this.inputTxtRef?.current) {
                this.inputTxtRef.current.value = "";
              }
            }}
            OptionContent={({ option, isHighlighted }) => {
              const cl = "tw-px-2 tw-py-1 tw-cursor-pointer";
              const highlightCl = isHighlighted ? "tw-bg-blueGray-200" : "";
              return (
                <div className={`${cl} ${highlightCl}`}>{option.name}</div>
              );
            }}
            OptionsWrapper={({ children }) => (
              <div className="tw-max-h-60 tw-bg-white tw-overflow-auto tw-mt-2 rounded-lg tw-shadow-lg tw-border border-1">
                {children}
              </div>
            )}
          >
            <div>
              <input
                id="phenotype-selector"
                ref={this.inputTxtRef}
                onChange={(e) => this.getSearch(e.target.value)}
                placeholder="Enter a phenotype..."
                type="text"
                className="tw-h-8 tw-px-2 tw-w-full tw-border tw-border-blueGray-200"
              />
            </div>
          </Autocomplete>
        </div>
      </div>
    );
  }
}

HPOSelector.propTypes = {
  hpo: PropTypes.shape({
    diseases: PropTypes.arrayOf(PropTypes.shape({})),
    phenotypes: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  addDisease: PropTypes.func.isRequired,
  addPhenotype: PropTypes.func.isRequired,
  selectedSource: PropTypes.arrayOf(PropTypes.string),
};

HPOSelector.defaultProps = {
  hpo: { diseases: [], phenotypes: [] },
  selectedSource: [],
};

export default HPOSelector;
